import DOMPurify from "dompurify";

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const decodeHtmlEntities = (value: string) => {
  if (typeof document === "undefined") return value;

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

const sanitizeHtml = (value: string) =>
  DOMPurify.sanitize(value, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["target", "rel", "class", "style"],
  });

const addLinkSafety = (html: string) =>
  html.replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer" ');

const hasHtmlMarkup = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const normalizeFieldName = (name?: string) => (name || "").trim().toLowerCase();

export const isSurveyTitleField = (name?: string) =>
  /(?:^|[.\[\]_\-:])title$/.test(normalizeFieldName(name)) ||
  /title$/.test(normalizeFieldName(name));

export const isSurveyDescriptionField = (name?: string) =>
  /(?:^|[.\[\]_\-:])description$/.test(normalizeFieldName(name)) ||
  /description$/.test(normalizeFieldName(name));

export const isSurveyFormattedField = (name?: string) =>
  isSurveyTitleField(name) || isSurveyDescriptionField(name);

export const shouldRenderSurveyFormattedText = (
  value?: string | null,
  name?: string,
) => {
  if (isSurveyFormattedField(name)) return true;
  if (name) return false;

  const normalizedValue = decodeHtmlEntities(value || "").trim();
  return Boolean(normalizedValue && hasHtmlMarkup(normalizedValue));
};

export const renderSurveyFormattedTextToHtml = (
  value?: string | null,
  variant: "inline" | "block" = "block",
) => {
  if (!value) return "";

  const normalizedValue = decodeHtmlEntities(value).trim();
  if (!normalizedValue) return "";

  if (hasHtmlMarkup(normalizedValue)) {
    return addLinkSafety(sanitizeHtml(normalizedValue));
  }

  const escaped = escapeHtml(normalizedValue).replace(
    /\r?\n/g,
    variant === "inline" ? "<br />" : "</p><p>",
  );

  return variant === "inline" ? escaped : `<p>${escaped}</p>`;
};
